import { useDataProvider } from 'react-admin';
import {
  JobPhaseReceivableCreatableInterface,
  JobPhaseReceivableDto,
  JobPhaseReceivableUpdatableInterface,
} from '@vatos-pas/common';

type ReceivableBooleanFields = Pick<
  JobPhaseReceivableUpdatableInterface,
  'supplyProIntegrated' | 'billed'
>;

type ReceivableInvoiceFields = Pick<
  JobPhaseReceivableUpdatableInterface,
  'invoicePOAmount' | 'internalInvoiceNumber'
>;

export type ReceivableCurrentSnapshot = Required<ReceivableBooleanFields> &
  ReceivableInvoiceFields;

export type ReceivableChanges = Partial<
  ReceivableBooleanFields & ReceivableInvoiceFields
>;

export type SaveArgs = {
  jobPhaseId: string;
  jobPhaseReceivableId: string | null | undefined;
  changes: ReceivableChanges;
  current: ReceivableCurrentSnapshot;
};

const RESOURCE = 'job-phase-receivable';

export const useReceivablePersistence = (): {
  save: (args: SaveArgs) => Promise<JobPhaseReceivableDto>;
} => {
  const dataProvider = useDataProvider();

  const save = async ({
    jobPhaseId,
    jobPhaseReceivableId,
    changes,
    current,
  }: SaveArgs): Promise<JobPhaseReceivableDto> => {
    if (!jobPhaseReceivableId) {
      const createData: JobPhaseReceivableCreatableInterface = {
        jobPhaseId,
        supplyProIntegrated:
          changes.supplyProIntegrated ?? current.supplyProIntegrated,
        billed: changes.billed ?? current.billed,
      };

      if (changes.invoicePOAmount !== undefined) {
        createData.invoicePOAmount = changes.invoicePOAmount;
      }
      if (changes.internalInvoiceNumber !== undefined) {
        createData.internalInvoiceNumber = changes.internalInvoiceNumber;
      }

      const response = await dataProvider.create<JobPhaseReceivableDto>(
        RESOURCE,
        { data: createData },
      );
      return response.data;
    }

    const updateData: JobPhaseReceivableUpdatableInterface = changes;
    const response = await dataProvider.update<JobPhaseReceivableDto>(RESOURCE, {
      id: jobPhaseReceivableId,
      data: updateData,
      previousData: {
        id: jobPhaseReceivableId,
        ...current,
      },
    });
    return response.data;
  };

  return { save };
};
